import { iterate } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { writeFile } from "../../storage/fsa.ts";
import { getCursor, putCursor, itemNeedsExport, putItem, SINGLE_CURSOR } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import { contactRawPath } from "./paths.ts";
import { renderContactFiles, renderContactsIndex } from "./disk-render.ts";
import type { Contact } from "./types.ts";

/**
 * Contacts export (ARCHITECTURE.md §15 step 8).
 *
 * `/me/contacts/delta` covers the default Contacts folder only — the same
 * flat scope the documented output (`contacts/contacts.vcf` /
 * `contacts.csv`, one file each, no per-folder split) implies. Custom
 * contact folders (`/me/contactFolders`) are not walked; this mirrors Mail's
 * own documented simplification of not yet supporting a shared/secondary
 * mailbox — additive later, not a redesign.
 *
 * One cursor for the whole module, like OneDrive: delta returns every
 * contact in one paged, resumable stream, so there is nothing to key a
 * cursor per-container the way Mail (per folder) or Teams (per chat) need.
 *
 * Each contact is written as its own raw JSON file — the per-item resumable
 * unit, exactly like Mail's .eml or a Teams message — and the two real
 * output files are regenerated from whatever raw files are on disk once the
 * delta walk finishes. A contact deleted since the last pass (`@removed`) is
 * neither fetched nor deleted locally, the same "backup, not mirror" policy
 * Mail and OneDrive already apply to their own deletions.
 */

const SCOPES = MODULE_SCOPES.contacts;

export interface ContactsProgress {
    contactsDone: number;
    currentLabel?: string;
    totalBytes: number;
}

export type ContactsProgressCallback = (progress: ContactsProgress) => void;

export interface ExportContactsParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    onProgress?: ContactsProgressCallback;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

export interface ContactsResult {
    contacts: number;
    bytes: number;
    errors: number;
}

export async function exportContacts(params: ExportContactsParams): Promise<ContactsResult> {
    const { runId, root, onProgress, signal, logger } = params;

    const cursor = await getCursor(runId, "contacts", SINGLE_CURSOR);
    const startUrl = cursor?.deltaLink ?? cursor?.nextLink ?? "/me/contacts/delta";

    let contacts = 0;
    let bytes = 0;
    let errors = 0;
    let deltaLink: string | undefined;

    await logger.info("starting contacts delta walk");

    for await (const contact of iterate<Contact>(startUrl, {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {}),
        // Kept until the walk ends rather than written here: it is only safe
        // to continue from once every contact in this pass was written (see
        // the putCursor below the loop).
        onDeltaLink: (link) => {
            deltaLink = link;
        },
        onNextLink: (nextLink) => {
            void putCursor({ runId, module: "contacts", subKey: SINGLE_CURSOR, nextLink, done: false });
        }
    })) {
        throwIfAborted(signal);

        if (contact["@removed"]) {
            continue;
        }

        const itemId = contact.id;
        // An updated contact comes back through delta with its full new
        // state, which replaces the earlier raw file (and so the .vcf/.csv).
        const version = contact.lastModifiedDateTime ?? contact.changeKey;
        if (!(await itemNeedsExport(runId, "contacts", itemId, version))) {
            continue;
        }

        try {
            const label = contact.displayName || contact.id;
            onProgress?.({ contactsDone: contacts, currentLabel: label, totalBytes: bytes });

            const written = await writeFile(root, contactRawPath(contact.id), JSON.stringify(contact));
            await putItem({
                runId,
                module: "contacts",
                itemId,
                status: "done",
                bytes: written,
                ...(version !== undefined ? { version } : {})
            });

            contacts += 1;
            bytes += written;
        } catch (err) {
            if (
                err instanceof ExportAbortedError ||
                err instanceof InteractionRequiredError ||
                err instanceof NotSignedInError
            ) {
                throw err;
            }
            errors += 1;
            await logger.error(`contact failed, will retry on resume: ${String(err)} (${itemId})`);
        }
    }

    // The deltaLink is kept only after an error-free pass. A contact that
    // failed is not marked done, but a deltaLink only reports *later* changes,
    // so continuing from it would never offer that contact again; without one
    // the next run walks from the start and retries it (done ones are skipped).
    // This write used to omit the deltaLink altogether, silently replacing the
    // one saved moments earlier, so every run was a full walk.
    await putCursor({
        runId,
        module: "contacts",
        subKey: SINGLE_CURSOR,
        done: true,
        ...(deltaLink && errors === 0 ? { deltaLink } : {})
    });

    // Idempotent and network-free — safe whether the walk just finished, was
    // interrupted partway, or found nothing new at all.
    const allContacts = await renderContactFiles(root);
    await renderContactsIndex(root, allContacts);

    await logger.info(`contacts export complete: ${contacts} contacts, ${errors} errors`);

    return { contacts, bytes, errors };
}
